/**
 * Transforms a GET /v1/course-versions/tree response (the full authored course
 * structure) into the legacy course-tree shape that `findNode`,
 * `buildLeafNodeIdMap` and friends already understand.
 *
 * Unlike the audienced structure, the authored structure contains every node
 * the course was authored with, so leaf node ids resolve regardless of the
 * caller's audience.
 *
 * The endpoint is queried with `format=both`, which returns a recursive `tree`
 * alongside a `flat` payload. The recursive `tree` is documented as opaque, so
 * we prefer it only when it actually nests; otherwise we rebuild the hierarchy
 * from `flat.nodes`, whose shape is pinned by the response schema.
 *
 * Returns null when neither representation is usable.
 */
export function transformAuthoredCourseTree(response) {
  if (!response || typeof response !== 'object') return null

  // Guard against treating the response envelope itself as a tree node, which
  // would yield a root with no contentfulId and no children.
  const hasTree = hasOwn(response, 'tree')
  if (!hasTree && !hasOwn(response, 'flat')) return null

  const nestedTree = hasTree ? transformNestedNode(response.tree) : null
  if (nestedTree?.children?.length) return nestedTree

  return buildTreeFromFlatNodes(response.flat) || nestedTree
}

/**
 * Maps the recursive `tree` payload with the same field mapping used for flat
 * nodes. This deliberately does not reuse `transformCurriculumCourseTree`: that
 * transform is scoped to the audienced endpoint's camelCase payload, whereas the
 * authored endpoint also emits snake_case fields.
 */
function transformNestedNode(node) {
  if (!node || typeof node !== 'object') return null

  const transformed = toLegacyNode(node)
  if (Array.isArray(node.children)) {
    transformed.children = node.children
      .map(transformNestedNode)
      .filter(Boolean)
  }
  return transformed
}

/**
 * Transforms a map of `{ [courseCode]: authoredResponse }` into a map of
 * `{ [courseCode]: legacyTree }`, matching `transformCurriculumCourseTrees`.
 */
export function transformAuthoredCourseTrees(authoredCourseMap) {
  if (!authoredCourseMap || typeof authoredCourseMap !== 'object') return {}

  const trees = {}
  for (const [courseCode, payload] of Object.entries(authoredCourseMap)) {
    const tree = transformAuthoredCourseTree(payload)
    if (tree) trees[courseCode] = tree
  }
  return trees
}

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key)
}

function buildTreeFromFlatNodes(flat) {
  const nodes = flat?.nodes
  if (!Array.isArray(nodes) || nodes.length === 0) return null

  const nodesByElementId = new Map()
  for (const node of nodes) {
    const elementId = readElementId(node)
    if (elementId == null) continue
    nodesByElementId.set(elementId, { ...toLegacyNode(node), children: [] })
  }

  const roots = []
  for (const node of nodes) {
    const built = nodesByElementId.get(readElementId(node))
    if (!built) continue

    const parentId = readParentElementId(node)
    const parent = parentId == null ? null : nodesByElementId.get(parentId)
    if (parent) {
      parent.children.push(built)
    } else {
      roots.push(built)
    }
  }

  // A well-formed authored structure has exactly one parentless node. If the
  // response is partial we take the shallowest candidate so the deepest
  // possible subtree is still traversable.
  const root = roots.length > 1 ? roots.sort(byDepth)[0] : roots[0]
  if (!root) return null

  sortChildren(root)
  return root
}

function toLegacyNode(node) {
  const leafNodeId = readLeafExternalId(node)
  return {
    ...node,
    contentfulId: node.externalId ?? node.external_id ?? null,
    // `leaf` is how the legacy shape marks terminal nodes, and helpers such as
    // utils/program/training/progress collect leaves with `node.leaf === true`.
    // Fall back to the presence of a leaf external id for payloads that omit
    // the explicit flag.
    leaf: node.leaf ?? node.isLeaf ?? node.is_leaf ?? !!leafNodeId,
    leafNodeId,
    requirementType: node.requirementType ?? node.requirement_type ?? null,
    displayTitle: node.displayTitle ?? node.display_title ?? null,
  }
}

function readLeafExternalId(node) {
  return node?.leafExternalId ?? node?.leaf_external_id ?? null
}

function sortChildren(node) {
  node.children.sort(bySortOrder)
  for (const child of node.children) {
    sortChildren(child)
  }
}

function bySortOrder(a, b) {
  return readSortOrder(a) - readSortOrder(b)
}

function byDepth(a, b) {
  return (a.depth ?? 0) - (b.depth ?? 0)
}

function readElementId(node) {
  return node?.courseElementId ?? node?.course_element_id ?? null
}

function readParentElementId(node) {
  return node?.parentCourseElementId ?? node?.parent_course_element_id ?? null
}

function readSortOrder(node) {
  return node?.sortOrder ?? node?.sort_order ?? 0
}
